import { createApi } from '@reduxjs/toolkit/query/react'
import { handleError } from '../../commons/utils/utils'
import {
  setBusinessUser,
  setProgramDetail
} from '../slices/transitValidationSlice'
import { generateRPCQuery, getBaseQuery } from './apiUtils'
import apiConstants from './constants'

export const transitValidationApi = createApi({
  reducerPath: 'transitValidationApi',
  refetchOnFocus: false,
  baseQuery: getBaseQuery(),
  tagTypes: [
    'BusinessUser',
    'BusinessUserDetails',
    'AllBusinessUsers',
    'ProgramDetails',
    'ProgramCodes',
    'AllCreatedCodes',
    'ScannedReceipts',
    'AllProgramsByBusinessUserID'
  ],
  endpoints: (builder) => ({
    createBusinessUser: builder.mutation({
      query: (requestParams: BusinessUserFormParams) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'createBusinessUser',
          requestParams
        )
    }),
    validateBusinessUser: builder.mutation({
      query: (requestParams: BusinessUserValidationParams) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'validationForBusinessUser',
          requestParams
        )
    }),
    getBusinessUserDetailsByID: builder.query({
      query: (requestParams: BusinessUserDetailsParams) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'getBusinessUserByID',
          requestParams
        ),
      providesTags: [{ type: 'BusinessUserDetails' }],
      async onQueryStarted ({}, { dispatch, queryFulfilled }) {
        try {
          const { data } = (await queryFulfilled) as {
            data?: GetBusinessUserDetailsResponse
          }
          if (data?.result) {
            dispatch(setBusinessUser(data.result))
          }
        } catch (err: any) {
          handleError(err, dispatch)
        }
      }
    }),
    suspendBusinessUser: builder.mutation({
      query: (requestParams: BusinessUserOptionsUpdateParams) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'suspendBusinessUser',
          requestParams
        ),
      invalidatesTags: [{ type: 'BusinessUserDetails' }]
    }),
    reactivateBusinessUser: builder.mutation({
      query: (requestParams: BusinessUserOptionsUpdateParams) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'reactivateBusinessUser',
          requestParams
        ),
      invalidatesTags: [{ type: 'BusinessUserDetails' }]
    }),
    editBusinessUser: builder.mutation({
      query: (requestParams: BusinessUserFormParams) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'editBusinessUser',
          requestParams
        ),
      invalidatesTags: [{ type: 'BusinessUserDetails' }]
    }),
    updateBusinessUserLocation: builder.mutation({
      query: (requestParams: BusinessUserLocationUpdateParams) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'updateBusinessUserLocation',
          requestParams
        ),
      invalidatesTags: [{ type: 'BusinessUserDetails' }]
    }),
    createProgram: builder.mutation({
      query: (requestParams: ProgramFormParams) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'createProgram',
          requestParams
        )
    }),
    editProgram: builder.mutation({
      query: (requestParams: ProgramFormParams) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'editProgram',
          requestParams
        ),
      invalidatesTags: [
        { type: 'AllCreatedCodes' },
        { type: 'ProgramDetails' },
        { type: 'ProgramCodes' }
      ]
    }),
    validateProgram: builder.mutation({
      query: (requestParams: ProgramValidationParams) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'validationForProgram',
          requestParams
        )
    }),
    getAllBusinesses: builder.query({
      query: (requestParams: { TenantID: string }) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'getAllBusinesses',
          requestParams
        )
    }),
    getProgramDetailsByID: builder.query({
      query: (requestParams: { ProgramID: string }) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'getProgramDetailsByID',
          requestParams
        ),
      providesTags: [{ type: 'ProgramDetails' }],
      async onQueryStarted ({}, { dispatch, queryFulfilled }) {
        try {
          const { data } = (await queryFulfilled) as {
            data?: { result: BaseProgram }
          }
          if (data?.result) {
            dispatch(setProgramDetail(data.result))
          }
        } catch (err: any) {
          handleError(err, dispatch)
        }
      }
    }),
    getAllCodesByProgramID: builder.query({
      query: (requestParams: { ProgramID: number }) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'getAllCodesByProgramID',
          requestParams
        ),
      providesTags: [{ type: 'AllCreatedCodes' }]
    }),
    deleteCodeByID: builder.mutation({
      query: (requestParams: DeleteProgramCodesParams) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'deleteCodeByID',
          requestParams
        ),
      invalidatesTags: [
        { type: 'AllCreatedCodes' },
        { type: 'ProgramDetails' },
        { type: 'ProgramCodes' }
      ]
    }),
    getAllProgramsByBusinessUserID: builder.query({
      query: (requestParams: GetAllProgramsByBusinessUserIDParams) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'getAllProgramsByBusinessUserID',
          requestParams
        ),
      providesTags: [{ type: 'AllProgramsByBusinessUserID' }]
    }),
    getReceiptsByProgramID: builder.query({
      query: (requestParams: { ProgramID: number }) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'getReceiptsByProgramID',
          requestParams
        )
    }),
    searchScannedReceipts: builder.query({
      query: (requestParams: SearchScannedReceiptsParams) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'searchScannedReceipts',
          requestParams
        ),
      providesTags: [{ type: 'ScannedReceipts' }]
    }),
    getBMProductsForTransitValidation: builder.query({
      query: (requestParams: { TenantID: string }) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'getBMProductsForTransitValidation',
          requestParams
        )
    }),
    deleteProgram: builder.mutation({
      query: (requestParams: DeleteProgramParams) =>
        generateRPCQuery(
          apiConstants.TRANSIT_VALIDATION_RPC_PATH,
          'deleteProgram',
          requestParams
        ),
      invalidatesTags: [
        { type: 'AllProgramsByBusinessUserID' },
        { type: 'ProgramDetails' },
        { type: 'AllCreatedCodes' },
        { type: 'ProgramCodes' }
      ]
    })
  })
})

export const {
  useCreateBusinessUserMutation,
  useEditBusinessUserMutation,
  useValidateBusinessUserMutation,
  useGetBusinessUserDetailsByIDQuery,
  useSuspendBusinessUserMutation,
  useReactivateBusinessUserMutation,
  useUpdateBusinessUserLocationMutation,
  useCreateProgramMutation,
  useEditProgramMutation,
  useValidateProgramMutation,
  useGetAllBusinessesQuery,
  useGetProgramDetailsByIDQuery,
  useDeleteCodeByIDMutation,
  useGetAllCodesByProgramIDQuery,
  useGetAllProgramsByBusinessUserIDQuery,
  useGetReceiptsByProgramIDQuery,
  useSearchScannedReceiptsQuery,
  useGetBMProductsForTransitValidationQuery,
  useDeleteProgramMutation
} = transitValidationApi
